import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { NaturezaOperacaoCompletaRequest } from '../../requests/motor-fiscal/natureza-operacao-completa.request';
import { NaturezaOperacaoBulkDeleteRequest } from '../../requests/motor-fiscal/natureza-operacao-bulk-delete.request';
import { ListaNaturezasOperacaoResponse } from '../../responses/motor-fiscal/lista-naturezas-operacao.response';
import { NaturezaOperacaoLookupResponse } from '../../responses/motor-fiscal/natureza-operacao-lookup.response';
import { NaturezaOperacaoDetalheResponse } from '../../responses/motor-fiscal/natureza-operacao-detalhe.response';
import { NaturezaOperacaoCompletaResponse } from '../../responses/motor-fiscal/natureza-operacao-completa.response';
import { NaturezaOperacaoSalvarResponse } from '../../responses/motor-fiscal/natureza-operacao-salvar.response';
import { NaturezaRegrasVinculadasResponse } from '../../responses/motor-fiscal/natureza-regras-vinculadas.response';
import { NaturezaOperacaoSeedRequest } from '../../requests/motor-fiscal/natureza-operacao-seed.request';
import { ListaNaturezaOperacaoPorEmissorResponse } from '../../responses/motor-fiscal/lista-natureza-operacao-por-emissor.response';
import { ReatribuirEmissorNaturezaOperacaoRequest } from '../../requests/motor-fiscal/reatribuir-emissor-natureza-operacao.request';
import { DefinirPadraoNaturezaOperacaoRequest } from '@shared/services/requests/motor-fiscal/definir-padrao-natureza-operacao.request';

@Injectable({ providedIn: 'root' })
export class NaturezaOperacaoService extends CommonService {
    private readonly apiUrl: string = environment.urlApiMotorFiscal + 'NaturezaOperacao';

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getAll(
        pesquisa: string | null,
        idTipoOperacao: number | null,
        paginaAtual: number,
        tamanhoPagina: number,
        idRegimeTributarioEmpresa: number | null = null
    ): Observable<ListaNaturezasOperacaoResponse> {
        let params = new HttpParams()
            .set('paginaAtual', paginaAtual.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        if (pesquisa) params = params.set('pesquisa', pesquisa);
        if (idTipoOperacao) params = params.set('idTipoOperacao', idTipoOperacao.toString());
        if (idRegimeTributarioEmpresa != null) {
            params = params.set('idRegimeTributarioEmpresa', idRegimeTributarioEmpresa.toString());
        }

        return this.httpClient.get<ListaNaturezasOperacaoResponse>(`${this.apiUrl}/all`, {
            params,
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /**
     * Naturezas ativas para o passo "Naturezas" do cadastro da regra tributária. Lookup sem permissão de rotina:
     * a regra fica em Reforma Tributária e a natureza em Notas Fiscais.
     */
    public getLookup(
        pesquisa: string | null,
        idTipoOperacao: number | null,
        paginaAtual: number,
        tamanhoPagina: number
    ): Observable<NaturezaOperacaoLookupResponse> {
        let params = new HttpParams()
            .set('paginaAtual', paginaAtual.toString())
            .set('tamanhoPagina', tamanhoPagina.toString());

        if (pesquisa) params = params.set('pesquisa', pesquisa);
        if (idTipoOperacao) params = params.set('idTipoOperacao', idTipoOperacao.toString());

        return this.httpClient.get<NaturezaOperacaoLookupResponse>(`${this.apiUrl}/lookup`, {
            params,
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /**
     * Cria o catálogo inicial de naturezas da empresa. O regime tributário vai no request porque
     * esta API não alcança o db_usuario: quem o resolve é a tela, lendo o cadastro da empresa e,
     * na falta, perguntando ao usuário.
     */
    public seed(request: NaturezaOperacaoSeedRequest): Observable<ReturnStruct> {
        return this.httpClient.post<ReturnStruct>(`${this.apiUrl}/seed`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getById(id: number): Observable<NaturezaOperacaoDetalheResponse> {
        return this.httpClient.get<NaturezaOperacaoDetalheResponse>(`${this.apiUrl}/${id}`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public alterarStatus(id: number, isAtivo: boolean): Observable<ReturnStruct> {
        return this.httpClient.patch<ReturnStruct>(`${this.apiUrl}/${id}/status`, { isAtivo }, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public excluir(id: number): Observable<ReturnStruct> {
        return this.httpClient.delete<ReturnStruct>(`${this.apiUrl}/${id}`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public bulkDelete(request: NaturezaOperacaoBulkDeleteRequest): Observable<ReturnStruct> {
        return this.httpClient.post<ReturnStruct>(`${this.apiUrl}/bulk-delete`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getCompleto(id: number): Observable<NaturezaOperacaoCompletaResponse> {
        return this.httpClient.get<NaturezaOperacaoCompletaResponse>(`${this.apiUrl}/${id}/completo`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public criarCompleto(request: NaturezaOperacaoCompletaRequest): Observable<NaturezaOperacaoSalvarResponse> {
        return this.httpClient.post<NaturezaOperacaoSalvarResponse>(`${this.apiUrl}/completo`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public atualizarCompleto(id: number, request: NaturezaOperacaoCompletaRequest): Observable<NaturezaOperacaoSalvarResponse> {
        return this.httpClient.put<NaturezaOperacaoSalvarResponse>(`${this.apiUrl}/${id}/completo`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    public getRegrasVinculadas(id: number): Observable<NaturezaRegrasVinculadasResponse> {
        return this.httpClient.get<NaturezaRegrasVinculadasResponse>(`${this.apiUrl}/${id}/regras-vinculadas`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /** Substitui os tipos de padrão da natureza; tipo que é de outra natureza da empresa passa para esta. */
    public tornarPadrao(id: number, request: DefinirPadraoNaturezaOperacaoRequest): Observable<ReturnStruct> {
        return this.httpClient.patch<ReturnStruct>(`${this.apiUrl}/${id}/tornar-padrao`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /** Naturezas atuais da empresa que apontam para o emissor (db_dfe) informado. */
    public getPorEmissor(idEmissor: number): Observable<ListaNaturezaOperacaoPorEmissorResponse> {
        return this.httpClient.get<ListaNaturezaOperacaoPorEmissorResponse>(`${this.apiUrl}/idEmissor/${idEmissor}/naturezas`, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }

    /** Aponta todas as naturezas informadas para outro emissor, ou para nenhum (emissor padrão). */
    public reatribuirEmissor(request: ReatribuirEmissorNaturezaOperacaoRequest): Observable<ReturnStruct> {
        return this.httpClient.patch<ReturnStruct>(`${this.apiUrl}/reatribuir-emissor`, request, {
            ...this.addHeaderToken()
        }).pipe(catchError(this.handleError));
    }
}
